import type { ConductorItem } from './types'

/**
 * Lecture du déroulé comme une feuille de route : durées, chevauchements,
 * trous, heure de fin.
 *
 * Calculé ici et non stocké : ce sont des conséquences des heures saisies,
 * pas des données. Les stocker, c'était les voir diverger dès le premier
 * décalage.
 */

const MINUTE = 60_000

export type PlanWarning =
  | { kind: 'overlap'; minutes: number; with: string }
  | { kind: 'gap'; minutes: number }
  | { kind: 'order'; with: string }
  | { kind: 'untimed' }

export function durationMinutes(item: Pick<ConductorItem, 'starts_at' | 'ends_at'>): number | null {
  if (!item.starts_at || !item.ends_at) return null
  return Math.round((Date.parse(item.ends_at) - Date.parse(item.starts_at)) / MINUTE)
}

/**
 * Les alertes de chaque étape, comparée à l'étape horodatée qui la précède.
 *
 * Une étape sautée ne compte plus : son créneau est libre. Une étape sans
 * durée n'a pas de fin connue — on ne peut alors parler ni de trou ni de
 * chevauchement, seulement d'ordre.
 */
export function planWarnings(items: ConductorItem[]): Map<string, PlanWarning[]> {
  const warnings = new Map<string, PlanWarning[]>()
  let previous: ConductorItem | null = null

  for (const item of items) {
    const found: PlanWarning[] = []
    if (item.status === 'skipped') {
      warnings.set(item.id, found)
      continue
    }
    if (!item.starts_at) {
      found.push({ kind: 'untimed' })
      warnings.set(item.id, found)
      continue
    }

    if (previous?.starts_at) {
      const start = Date.parse(item.starts_at)
      const previousStart = Date.parse(previous.starts_at)
      if (start < previousStart) {
        found.push({ kind: 'order', with: previous.id })
      } else if (previous.ends_at) {
        const delta = Math.round((start - Date.parse(previous.ends_at)) / MINUTE)
        if (delta < 0) found.push({ kind: 'overlap', minutes: -delta, with: previous.id })
        if (delta > 0) found.push({ kind: 'gap', minutes: delta })
      }
    }

    warnings.set(item.id, found)
    previous = item
  }
  return warnings
}

/** L'heure à laquelle la dernière étape prévue se termine. */
export function plannedEnd(items: ConductorItem[]): string | null {
  let latest: number | null = null
  for (const item of items) {
    if (item.status === 'skipped') continue
    const edge = item.ends_at ?? item.starts_at
    if (!edge) continue
    const value = Date.parse(edge)
    if (latest === null || value > latest) latest = value
  }
  return latest === null ? null : new Date(latest).toISOString()
}

/**
 * Où « Décaler la suite » s'applique quand le MC le demande sans viser une
 * étape : la première encore à venir. Ce qui tourne ou est joué ne bouge pas.
 */
export function nextScheduled(items: ConductorItem[]): ConductorItem | null {
  return items.find((item) => item.status === 'scheduled' && item.starts_at) ?? null
}

/** Combien d'étapes un décalage depuis `pivot` déplacera — le serveur fait la même sélection. */
export function shiftCount(items: ConductorItem[], pivotId: string): number {
  const index = items.findIndex((item) => item.id === pivotId)
  if (index < 0) return 0
  return items.slice(index).filter((item) => item.status === 'scheduled' && item.starts_at).length
}

/** Fin de l'étape précédente : l'heure proposée pour une étape qu'on insère. */
export function suggestedStart(items: ConductorItem[], afterId: string | null): string | null {
  const index = afterId ? items.findIndex((item) => item.id === afterId) : items.length - 1
  for (let i = index; i >= 0; i -= 1) {
    const item = items[i]
    if (item?.ends_at) return item.ends_at
    if (item?.starts_at) return item.starts_at
  }
  return null
}

export interface LiveTiming {
  running: ConductorItem | null
  /** L'étape qui suit : la première encore à venir après celle qui tourne. */
  next: ConductorItem | null
  /** Minutes écoulées depuis le lancement RÉEL de l'étape en cours. */
  elapsed: number | null
  /** Sa durée prévue, quand le déroulé en donne une. */
  planned: number | null
  /**
   * Le retard projeté de l'étape suivante sur son heure prévue, en minutes
   * (négatif : de l'avance). `null` quand elle n'a pas d'heure.
   */
  delay: number | null
}

/**
 * Où en est la soirée par rapport au déroulé — ce que l'en-tête du conducteur
 * affiche en permanence.
 *
 * La suite ne peut pas commencer avant que l'étape en cours ait eu sa durée
 * prévue, ni avant maintenant : c'est cette heure projetée, comparée à l'heure
 * prévue de l'étape suivante, qui fait le retard. Sans rien en cours, on
 * attend la suite : on est en retard si son heure est passée, jamais « en
 * avance » d'une attente.
 */
export function liveTiming(items: ConductorItem[], now: number): LiveTiming {
  const runningIndex = items.findIndex((item) => item.status === 'running')
  const running = runningIndex >= 0 ? (items[runningIndex] ?? null) : null
  const next =
    items.slice(runningIndex + 1).find((item) => item.status === 'scheduled') ??
    items.find((item) => item.status === 'scheduled') ??
    null

  const startedAt = running?.actual_started_at ? Date.parse(running.actual_started_at) : null
  const elapsed = startedAt === null ? null : Math.max(0, Math.floor((now - startedAt) / MINUTE))
  const planned = running ? durationMinutes(running) : null

  let delay: number | null = null
  if (next?.starts_at) {
    const plannedStart = Date.parse(next.starts_at)
    let projected = now
    if (startedAt !== null && planned !== null) {
      projected = Math.max(now, startedAt + planned * MINUTE)
    }
    delay = Math.round((projected - plannedStart) / MINUTE)
    if (!running) delay = Math.max(0, delay)
  }
  return { running, next, elapsed, planned, delay }
}
